/**
 * Hearst attribution capture (WS1)
 * ---------------------------------
 * Reads ?source=hearst-<title>&medium=<...> from the URL, stores a FIRST-TOUCH first-party
 * cookie (`he_attrib`), and mirrors it into the Shopify cart as private attributes so it rides
 * through to the Order (note_attributes / customAttributes).
 *
 * NB: the Impact click id (irclickid) is intentionally NOT captured here — the impact.com
 * Shopify app captures it automatically onto the order and posts the conversion itself.
 *
 * Consent: writes nothing until Shopify's Customer Privacy API reports marketing consent.
 * If consent is collected later (banner interaction), it re-runs automatically.
 *
 * Deploy as a Shopify **theme app embed / custom Liquid block** (paste inside <script>),
 * OR as a **GTM Custom HTML tag** (firing on All Pages). No build step, no dependencies.
 *
 * Naming conventions are the single source of truth — keep in sync with README.md.
 */
(function () {
  "use strict";

  var CONFIG = {
    cookieName: "he_attrib",
    cookieMaxAgeDays: 30,
    sourcePrefix: "hearst-", // only ?source values starting with this are captured
    // Consent category required before we may store the cookie / cart attribute.
    // "marketing" is correct for partner/affiliate attribution (Impact). Options:
    // "marketing" | "analytics" | "any" (analytics OR marketing) | "none" (skip gating — NOT recommended on UK store)
    requireConsent: "marketing",
    // Private cart attribute keys (double underscore hides them from the checkout UI,
    // but they remain visible in Admin + Admin API / webhooks).
    attrKeys: {
      title: "__hearst_title",
      medium: "__hearst_medium",
      cid: "__hearst_cid"
    },
    syncSessionFlag: "he_cart_synced", // sessionStorage guard so we sync the cart once per session
    // Mirror the captured source to Triple Whale as a custom event (analytics only — does NOT
    // replace TW's native funnel/UTM/LTV tracking, it adds the Hearst dimension on top).
    // Define a matching event in TW: Settings → Pixel Settings → Custom Events → "hearstAttributed".
    triple: { eventName: "hearstAttributed", sessionFlag: "he_tw_fired", maxTries: 20 }
  };

  // ---- tiny helpers -------------------------------------------------------

  function log() {
    if (window.__HEARST_DEBUG__ && window.console) {
      console.log.apply(console, ["[hearst]"].concat([].slice.call(arguments)));
    }
  }

  function getParam(name) {
    try {
      return new URLSearchParams(window.location.search).get(name);
    } catch (e) {
      return null;
    }
  }

  function readCookie(name) {
    var m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    return m ? decodeURIComponent(m[1]) : null;
  }

  function writeCookie(name, value, maxAgeDays) {
    var maxAge = Math.floor(maxAgeDays * 24 * 60 * 60);
    var secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie =
      name + "=" + encodeURIComponent(value) +
      "; Max-Age=" + maxAge + "; Path=/; SameSite=Lax" + secure;
  }

  function uuid() {
    try {
      if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    } catch (e) {}
    return "hx-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
  }

  // ---- consent ------------------------------------------------------------

  function consentGranted() {
    if (CONFIG.requireConsent === "none") return true;
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (!cp) return false; // API not loaded yet → treat as not-yet-granted
    try {
      var marketing = cp.marketingAllowed && cp.marketingAllowed();
      var analytics = cp.analyticsProcessingAllowed && cp.analyticsProcessingAllowed();
      if (CONFIG.requireConsent === "marketing") return !!marketing;
      if (CONFIG.requireConsent === "analytics") return !!analytics;
      if (CONFIG.requireConsent === "any") return !!(marketing || analytics);
    } catch (e) {
      log("consent check failed", e);
    }
    return false;
  }

  // ---- capture ------------------------------------------------------------

  // First touch: only writes the cookie if it is not already present.
  function captureFirstTouch() {
    if (readCookie(CONFIG.cookieName)) {
      log("cookie already set (first-touch preserved)");
      return readCookie(CONFIG.cookieName);
    }
    var source = getParam("source");
    if (!source || source.indexOf(CONFIG.sourcePrefix) !== 0) return null;

    var record = {
      t: source.slice(CONFIG.sourcePrefix.length), // "menshealth"
      m: (getParam("medium") || "digital-web").toLowerCase(), // fallback when a link omits ?medium
      ts: Date.now(),
      cid: uuid()
    };
    var json = JSON.stringify(record);
    writeCookie(CONFIG.cookieName, json, CONFIG.cookieMaxAgeDays);
    try { sessionStorage.removeItem(CONFIG.syncSessionFlag); } catch (e) {}
    log("captured first touch", record);
    return json;
  }

  // Mirror the cookie into Shopify cart attributes (rides through to the Order).
  function syncToCart() {
    var raw = readCookie(CONFIG.cookieName);
    if (!raw) return;
    try {
      if (sessionStorage.getItem(CONFIG.syncSessionFlag) === "1") return;
    } catch (e) {}

    var rec;
    try { rec = JSON.parse(raw); } catch (e) { return; }

    var attributes = {};
    attributes[CONFIG.attrKeys.title] = rec.t || "";
    attributes[CONFIG.attrKeys.medium] = rec.m || "";
    attributes[CONFIG.attrKeys.cid] = rec.cid || "";

    fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attributes: attributes })
    })
      .then(function () {
        try { sessionStorage.setItem(CONFIG.syncSessionFlag, "1"); } catch (e) {}
        log("synced cart attributes", attributes);
      })
      .catch(function (e) { log("cart sync failed", e); });
  }

  // Mirror the captured source into Triple Whale as a custom event (analytics layer).
  // Page-context only: window.TriplePixel lives on the storefront page and is NOT reachable
  // from a sandboxed Shopify Web Pixel — which is exactly why this stays in the theme embed.
  // Fires once per session, behind the same consent gate as the cookie/cart write.
  function fireTriplePixel(rec) {
    if (!rec || !rec.t) return true; // nothing to report → treat as done
    try {
      if (sessionStorage.getItem(CONFIG.triple.sessionFlag) === "1") return true;
    } catch (e) {}
    if (typeof window.TriplePixel !== "function") return false; // not loaded yet → caller retries
    try {
      window.TriplePixel("custom", CONFIG.triple.eventName, {
        title: rec.t || "",
        medium: rec.m || "",
        cid: rec.cid || ""
      });
      try { sessionStorage.setItem(CONFIG.triple.sessionFlag, "1"); } catch (e) {}
      log("fired Triple Whale custom event", CONFIG.triple.eventName, rec);
      return true;
    } catch (e) {
      log("triple pixel fire failed", e);
      return true; // don't hammer on a hard error
    }
  }

  function mirrorToTriple() {
    var raw = readCookie(CONFIG.cookieName);
    if (!raw) return;
    var rec;
    try { rec = JSON.parse(raw); } catch (e) { return; }
    if (fireTriplePixel(rec)) return;
    // TriplePixel script not on the page yet — retry briefly until it loads.
    var n = 0;
    var t = setInterval(function () {
      n++;
      if (fireTriplePixel(rec) || n > CONFIG.triple.maxTries) clearInterval(t);
    }, 500);
  }

  function run() {
    if (!consentGranted()) {
      log("consent not granted yet — standing by");
      return;
    }
    captureFirstTouch();
    syncToCart();
    mirrorToTriple();
  }

  // ---- bootstrap ----------------------------------------------------------

  // Run now (in case consent already granted) and again whenever consent is collected.
  run();
  document.addEventListener("visitorConsentCollected", run);

  // Some themes load the Customer Privacy API late; retry briefly until it appears.
  var tries = 0;
  var poll = setInterval(function () {
    tries++;
    if (consentGranted() || tries > 20) {
      clearInterval(poll);
      if (consentGranted()) run();
    }
  }, 500);

  // Re-assert cart attributes after add-to-cart (cart may have been created/replaced).
  document.addEventListener("submit", function (e) {
    var form = e.target;
    if (form && form.getAttribute && /\/cart\/add/.test(form.getAttribute("action") || "")) {
      try { sessionStorage.removeItem(CONFIG.syncSessionFlag); } catch (err) {}
      setTimeout(syncToCart, 600);
    }
  });
})();
